import { isUuid } from "@/lib/ids";
import { ROUTES } from "@/lib/routes";
import { jobAddress, type ToolJob } from "@/lib/tools/pastedJob";

/**
 * The gates: every action that needs an account, or a plan, opens one
 * sheet named for the action (design/artboards/SiteMap.dc.html), and the
 * sign-in comes back to where the person was, with the action done.
 * Signing in is Google alone, through WorkOS.
 */
export type GateKind =
    | "save"
    | "tailor"
    | "tool_tailor"
    | "alerts"
    | "join"
    | "signin"
    | "upload"
    | "envoy"
    | "upgrade";

export const GATE_KINDS: readonly GateKind[] = [
    "save",
    "tailor",
    "tool_tailor",
    "alerts",
    "join",
    "signin",
    "upload",
    "envoy",
    "upgrade",
];

export interface GateCopy {
    title: string;
    line: string;
}

/** The upgrade sheet's line for someone on a plan, or not yet known to be on none. */
export const TAILORS_USED = "Your tailors for this month are used.";

/** Each sheet's title and line, as the boards have them. */
export const GATE_COPY: Record<GateKind, GateCopy> = {
    save: { title: "Save this job", line: "A free account keeps it on your Saved list." },
    tailor: {
        title: "Tailor my resume",
        line: "A free account and your resume. 10 tailored resumes a month, free.",
    },
    tool_tailor: {
        title: "Tailor this resume",
        line: "A free account. AI rewrites your resume for this job, from what is true of you. 10 tailored resumes a month, free.",
    },
    alerts: {
        title: "Get new matches by email",
        line: "A free account. Your envoy starts from this search.",
    },
    join: {
        title: "Create your free account",
        line: "Save your resume to autofill applications free forever. Choose AI tailoring when you need it.",
    },
    signin: { title: "Sign in", line: "Welcome back." },
    upload: { title: "Add your resume", line: "A free account keeps it and tailors from it." },
    envoy: {
        title: "Set up your envoy",
        line: "A free account. It looks for matches while you are away.",
    },
    upgrade: {
        title: "No tailors left this month",
        line: `${TAILORS_USED} A plan has more each month, and the stronger models.`,
    },
};

/** What to do once the person is back, signed in. */
export interface Intent {
    kind: GateKind;
    /** The address they were on, path and query. */
    path: string;
    /** The posting a Save or a Tailor was for. */
    postingId?: string;
    /** The job the free tool had, kept for the way back from sign-in. */
    pasted?: ToolJob;
}

const INTENT_KEY = "jobenvoy-intent-v1";

/** How long an intent waits for the sign-in to come back: longer is a different visit. */
const INTENT_TTL_MS = 30 * 60_000;

function isGateKind(value: unknown): value is GateKind {
    return typeof value === "string" && (GATE_KINDS as readonly string[]).includes(value);
}

/**
 * Whether a kept path stays on this site. A browser reads `\` as `/` and
 * drops a tab or a newline in an address, so `/\evil.test` and
 * `/<tab>/evil.test` are both `//evil.test`: another site. A `\` in the
 * query or the fragment is only a character there, so a search for it is kept.
 */
function isOwnPath(path: string): boolean {
    const pathname = path.split(/[?#]/)[0];
    return (
        pathname.startsWith("/") &&
        !pathname.startsWith("//") &&
        !pathname.includes("\\") &&
        // eslint-disable-next-line no-control-regex
        !/[\u0000-\u001f\u007f]/.test(path)
    );
}

/**
 * The job the tool kept: a posting's, if it names one by id with the
 * resume as text, or a pasted one, if every field is text and the link is
 * one `jobAddress` made. Anything else is dropped and nothing else with it.
 */
function pastedFrom(value: unknown): ToolJob | undefined {
    if (typeof value !== "object" || value === null) return undefined;
    const { postingId, title, company, address, description, resume } = value as Record<
        string,
        unknown
    >;
    if ("postingId" in value) {
        if (!isUuid(postingId as string) || typeof resume !== "string") return undefined;
        return { postingId: postingId as string, resume };
    }
    if (
        typeof title !== "string" ||
        typeof company !== "string" ||
        typeof address !== "string" ||
        typeof description !== "string" ||
        typeof resume !== "string"
    ) {
        return undefined;
    }
    if (jobAddress(address).address !== address) return undefined;
    return { title, company, address, description, resume };
}

/**
 * Kept for the sign-in's round trip, in this tab alone. A store too full
 * for a pasted resume keeps the intent without it, so the sign-in still
 * comes back to the page.
 */
export function keepIntent(storage: Storage | undefined, intent: Intent, now = Date.now()): void {
    const attempts = intent.pasted ? [intent, { ...intent, pasted: undefined }] : [intent];
    for (const attempt of attempts) {
        try {
            storage?.setItem(INTENT_KEY, JSON.stringify({ ...attempt, at: now }));
            return;
        } catch {
            // Private windows and full storage: the person lands where AuthKit sends them.
        }
    }
}

/** What was kept, if it reads: a known kind, a path on this site, and not past its time. */
function readIntent(raw: string, now: number): Intent | null {
    try {
        const parsed = JSON.parse(raw) as Record<string, unknown>;
        if (!isGateKind(parsed.kind) || typeof parsed.path !== "string") return null;
        if (!isOwnPath(parsed.path)) return null;
        if (typeof parsed.at !== "number" || now - parsed.at > INTENT_TTL_MS) return null;
        const pasted = parsed.kind === "tool_tailor" ? pastedFrom(parsed.pasted) : undefined;
        return {
            kind: parsed.kind,
            path: parsed.path,
            ...(typeof parsed.postingId === "string" ? { postingId: parsed.postingId } : {}),
            ...(pasted ? { pasted } : {}),
        };
    } catch {
        return null;
    }
}

/** The kept intent, once: it is gone after this read. Null when none, stale or unreadable. */
export function takeIntent(storage: Storage | undefined, now = Date.now()): Intent | null {
    let raw: string | null;
    try {
        raw = storage?.getItem(INTENT_KEY) ?? null;
        storage?.removeItem(INTENT_KEY);
    } catch {
        return null;
    }
    return raw ? readIntent(raw, now) : null;
}

/**
 * Removes a kept intent that is past its time or that does not read, so a
 * sign-in that was never finished leaves nothing behind. A fresh one stays
 * for the sign-in that is on its way back.
 */
export function dropStaleIntent(storage: Storage | undefined, now = Date.now()): void {
    try {
        const raw = storage?.getItem(INTENT_KEY) ?? null;
        if (raw !== null && readIntent(raw, now) === null) storage?.removeItem(INTENT_KEY);
    } catch {
        // Unreadable storage holds nothing we could clear.
    }
}

/** Which AuthKit screen a sheet opens: sign-in for Sign in, sign-up for every other. */
export function screenFor(kind: GateKind): "sign-in" | "sign-up" {
    return kind === "signin" ? "sign-in" : "sign-up";
}

/** Where a sheet's action lands once the person is signed in. */
export function landingFor(intent: Intent): string {
    switch (intent.kind) {
        case "alerts": {
            // The search it was asked from, for the envoy to start from.
            const search = intent.path.includes("?")
                ? intent.path.slice(intent.path.indexOf("?") + 1)
                : "";
            return `${ROUTES.ENVOY}?${new URLSearchParams({ seed: search })}`;
        }
        case "envoy":
            return ROUTES.ENVOY;
        case "upload":
            return `${ROUTES.RESUME}?upload=1`;
        default:
            return intent.path;
    }
}
